// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Utils
import { length } from '../../../../../common/utils/immutableHelper';

// Hooks
import useMouseHoverEnabledProperty from '../../../../utils/react/useMouseHoverEnabledProperty';

// Components
import PopupTrigger from '../../../../components/popupPanel/PopupTrigger';
import UserAvatarGroup from '../../../../components/userAvatar/UserAvatarGroup';

// Constants
import { USER_AVATAR_SIZES } from '../../../../../common/users/userConstants';

// Styles
import './BoardEditorAvatars.scss';

export const AVATAR_SPREAD_DIRECTIONS = {
    LEFT: 'left',
    RIGHT: 'right',
};

const AVATAR_TRANSITION_TIME = 300;
const DEFAULT_AVATAR_LIMIT = 5;

const IdentityPopupTriggerFaCC = ({ children }) => children(false);

const getTooltipOffset = () => -3;

const BoardEditorAvatars = (props) => {
    const {
        children,
        className,
        users,
        currentBoardId,
        userAvatarLimit = DEFAULT_AVATAR_LIMIT,
        userAvatarSize = USER_AVATAR_SIZES.SMALL,
        spreadDirection = AVATAR_SPREAD_DIRECTIONS.LEFT,

        popupId = 'editors-popup',
        onUserAvatarClick,
    } = props;

    // This is used to improve the performance of the animation when hovering the user avatars.
    //  We need to prevent pointer events on the avatars during the animation, otherwise it will
    //  be very janky, we then need to enable it after a timeout so the tooltips can show.
    const [enableAvatarPointerEvents, onMouseEnter, onMouseLeave] =
        useMouseHoverEnabledProperty(AVATAR_TRANSITION_TIME);

    const hiddenUserCount = Math.max(0, length(users) - userAvatarLimit);
    const showCount = hiddenUserCount >= 1;

    const visibleUserCount = length(users) - hiddenUserCount;

    const alwaysReady = (visibleUserCount === 1 && !children) || (visibleUserCount === 0 && !!children);

    const limit = showCount ? userAvatarLimit - 1 : userAvatarLimit;

    const classes = classNames('BoardEditorAvatars', className, {
        'pointer-ready': enableAvatarPointerEvents || alwaysReady,
        'spread-right': spreadDirection === AVATAR_SPREAD_DIRECTIONS.RIGHT,
    });

    const PopupTriggerComponent = !!popupId ? PopupTrigger : IdentityPopupTriggerFaCC;

    return (
        <div className={classes} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onUserAvatarClick}>
            {children}
            <PopupTriggerComponent popupId={popupId}>
                {(isOpen) => (
                    <>
                        {userAvatarLimit > 0 && (
                            <div className="user-avatar-group-container">
                                <UserAvatarGroup
                                    limit={limit}
                                    tooltipDelay={100}
                                    getTooltipOffset={getTooltipOffset}
                                    tooltipEnabled={!isOpen}
                                    currentBoardId={currentBoardId}
                                    size={userAvatarSize}
                                    users={users}
                                    hiddenUserCount={hiddenUserCount}
                                />
                            </div>
                        )}
                    </>
                )}
            </PopupTriggerComponent>
        </div>
    );
};

BoardEditorAvatars.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    className: PropTypes.string,
    users: PropTypes.array,
    userAvatarLimit: PropTypes.number,
    userAvatarSize: PropTypes.number,
    currentBoardId: PropTypes.string,
    onUserAvatarClick: PropTypes.func,

    spreadDirection: PropTypes.string,

    popupId: PropTypes.string,
};

export default BoardEditorAvatars;
